import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { lireBase64 } from './lectureBase64';
import { t } from '../i18n';
import type { ImageInput, ImageMediaType } from '../types';

// Briques d'import partagées (CV, fiche de poste, captures LinkedIn) — une seule implémentation.

export const MAX_FILE_BYTES = 10 * 1024 * 1024; // PDF/Word
export const MAX_IMAGES = 5;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // par capture

export type DocKind = 'pdf' | 'docx';
export interface PickedFile {
  uri: string;
  name: string;
  kind: DocKind;
}
export interface PickedImage extends ImageInput {
  uri: string;
}

export interface PickFileOutcome {
  file?: PickedFile;
  error?: string;
  canceled?: boolean;
}
export interface PickImagesOutcome {
  images?: PickedImage[];
  error?: string;
  canceled?: boolean;
}

const ALLOWED_IMAGE: ImageMediaType[] = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

function detectKind(mimeType: string | undefined, name: string): DocKind | null {
  if (mimeType === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) return 'pdf';
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.toLowerCase().endsWith('.docx')
  ) {
    return 'docx';
  }
  return null;
}

function toMediaType(mime?: string): ImageMediaType {
  return (ALLOWED_IMAGE.find((m) => m === mime) ?? 'image/jpeg') as ImageMediaType;
}

// Sélection d'un document PDF/Word (CV ou fiche de poste).
/**
 * Sélecteur de document PDF ou Word, commun à toutes les features.
 *
 * Anciennement `pickCvFile` : le nom mentait depuis longtemps, la fonction servant aussi à la
 * fiche de poste, aux captures LinkedIn et désormais aux lettres de motivation.
 */
export async function pickDocumentFile(): Promise<PickFileOutcome> {
  const result = await DocumentPicker.getDocumentAsync({
    type: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    copyToCacheDirectory: true,
  });
  if (result.canceled) return { canceled: true };

  const asset = result.assets[0];
  const kind = detectKind(asset.mimeType, asset.name);
  if (!kind) return { error: 'Format non supporté. Choisis un PDF ou un Word (.docx).' };
  if ((asset.size ?? 0) > MAX_FILE_BYTES) return { error: 'Fichier trop lourd (max 10 Mo).' };
  return { file: { uri: asset.uri, name: asset.name, kind } };
}

// Sélection de captures d'écran en base64 (profil LinkedIn ou fiche de poste). `max` : le nombre de
// places restantes quand on AJOUTE à des captures déjà choisies (5 au total).
export async function pickImages(max: number = MAX_IMAGES): Promise<PickImagesOutcome> {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    base64: true,
    allowsMultipleSelection: true,
    selectionLimit: max,
  });
  if (result.canceled) return { canceled: true };

  const picked: PickedImage[] = [];
  let error: string | undefined;
  for (const asset of result.assets) {
    if (!asset.base64) continue;
    if ((asset.fileSize ?? 0) > MAX_IMAGE_BYTES) {
      error = 'Une capture dépasse 5 Mo. Choisis une image plus légère.';
      continue;
    }
    picked.push({ uri: asset.uri, media_type: toMediaType(asset.mimeType), data: asset.base64 });
  }
  return { images: picked.slice(0, max), error };
}

// ─── Fiche de poste de « Optimiser mon CV » (1.1.0) ───
/** Largeur maximale d'une capture envoyée : au-delà, Claude réduit de toute façon l'image. */
export const LARGEUR_MAX_CAPTURE = 1600;
/** Total des captures d'une fiche, après compression. */
export const MAX_CAPTURES_FICHE_BYTES = 8 * 1024 * 1024;
export const MAX_FICHE_TEXTE = 10_000;
export const MAX_FICHE_PAGES = 20;

type Manipulateur = typeof import('expo-image-manipulator');
let manipulateur: Manipulateur | null | undefined;

/**
 * CHARGÉ À LA DEMANDE, JAMAIS À L'IMPORT. `expo-image-manipulator` appelle `requireNativeModule` dès
 * son chargement et lève si le binaire ne le contient pas (binaires d'avant la 1.1.0, build de
 * développement pas encore reconstruit). Importé en tête de ce fichier, utilisé par presque tous les
 * écrans, il ferait planter l'app au démarrage : même leçon que le plantage ExpoAsset du 10/10/2026.
 */
function chargerManipulateur(): Manipulateur | null {
  if (manipulateur === undefined) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      manipulateur = require('expo-image-manipulator') as Manipulateur;
    } catch {
      manipulateur = null;
    }
  }
  return manipulateur;
}

/** Sans le module de redimensionnement, pas de captures de fiche : le bouton est masqué. */
export function capturesFichePossibles(): boolean {
  return chargerManipulateur() !== null;
}

/** Une capture redimensionnée (sans jamais l'agrandir) et réencodée en JPEG. */
async function compresserCapture(asset: ImagePicker.ImagePickerAsset, m: Manipulateur): Promise<PickedImage> {
  const { ImageManipulator, SaveFormat } = m;
  const contexte = ImageManipulator.manipulate(asset.uri);
  if ((asset.width ?? 0) > LARGEUR_MAX_CAPTURE) contexte.resize({ width: LARGEUR_MAX_CAPTURE });
  const image = await contexte.renderAsync();
  const resultat = await image.saveAsync({ format: SaveFormat.JPEG, compress: 0.7, base64: true });
  if (!resultat.base64) throw new Error('capture_sans_base64');
  return { uri: resultat.uri, media_type: 'image/jpeg', data: resultat.base64 };
}

/**
 * Captures d'une fiche de poste, à AJOUTER à celles déjà choisies : le sélecteur ne propose que ce
 * qui reste sous 5. Chaque image est réduite à 1 600 px de large au plus et réencodée en JPEG ;
 * l'original n'est jamais lu en base64 (il peut peser plusieurs Mo).
 */
export async function pickCapturesFiche(dejaChoisies: number): Promise<PickImagesOutcome> {
  const restantes = MAX_IMAGES - dejaChoisies;
  if (restantes <= 0) return { error: t('ficheDePoste.erreurs.tropDeCaptures', { max: MAX_IMAGES }) };
  const m = chargerManipulateur();
  if (!m) return { canceled: true }; // bouton masqué dans ce cas : défense en profondeur
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    allowsMultipleSelection: true,
    selectionLimit: restantes,
  });
  if (result.canceled) return { canceled: true };

  const images: PickedImage[] = [];
  let error: string | undefined;
  for (const asset of result.assets.slice(0, restantes)) {
    try {
      images.push(await compresserCapture(asset, m));
    } catch {
      error = t('ficheDePoste.erreurs.captureIllisible');
    }
  }
  return { images, error };
}

export interface PickProfileImageOutcome {
  image?: PickedImage;
  error?: string;
  canceled?: boolean;
}

// Sélection d'UNE photo pour le profil, en base64, avec recadrage interactif.
// Avatar : carré [1,1]. Bannière : rectangle [3,1] — la cible 'banner' n'est plus demandée par
// aucun écran depuis le retrait de la bannière, mais la convention de chemin Storage la connaît
// toujours (cf. scripts/purgeBanners.js), on ne restreint donc pas le type.
// NB iOS : `aspect` n'est honoré que sur Android ; iOS impose un crop carré quel que soit l'aspect.
export async function pickProfileImage(target: 'avatar' | 'banner'): Promise<PickProfileImageOutcome> {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    base64: true,
    allowsEditing: true,
    aspect: target === 'avatar' ? [1, 1] : [3, 1],
    quality: 0.8,
  });
  if (result.canceled) return { canceled: true };

  const asset = result.assets[0];
  if (!asset?.base64) return { error: 'Image illisible. Choisis une autre photo.' };
  if ((asset.fileSize ?? 0) > MAX_IMAGE_BYTES) return { error: 'Image trop lourde (max 5 Mo).' };
  return { image: { uri: asset.uri, media_type: toMediaType(asset.mimeType), data: asset.base64 } };
}

/** Charge utile d'un document importé, dans la forme qu'attendent tous les appels Claude. */
export type DocumentPayload = { pdf: { data: string } } | { docx: { data: string } };

/**
 * Résultat d'une lecture : la charge utile, OU un message prêt à afficher. Jamais les deux, jamais
 * aucun des deux.
 */
export interface LectureDocument {
  payload?: DocumentPayload;
  /** Présent si et seulement si `payload` est absent. */
  error?: string;
}

/**
 * FRONTIÈRE DE LECTURE D'UN DOCUMENT — et le point de ce fichier qui mérite d'être lu.
 *
 * CE QU'ELLE REMPLACE. `readFileBase64` était exportée et rendait `Promise<string>` : une lecture
 * qui échoue LEVAIT, et aucun de ses huit appelants n'était sous un `try`. Le rejet interrompait
 * le gestionnaire sans rien afficher — bouton qui ne fait rien, ou pire, `CvImproveScreen` figé sur
 * son écran de chargement parce que le rejet survenait avant tout `setPhase`. L'utilisateur
 * attendait au lieu de comprendre.
 *
 * POURQUOI PAS HUIT `try/catch`. Ce serait huit occasions d'oublier le neuvième. La réponse tenue
 * ici est celle qu'`AuthResult.errorKey` a déjà donnée ailleurs : ce n'est pas la forme du retour
 * seule qui ferme la fuite, c'est qu'IL N'EXISTE PLUS D'AUTRE CHEMIN. La lecture brute vit dans
 * `lectureBase64`, dont les deux faces de plateforme rendent `null` plutôt que de lever : même
 * importée directement, elle ne peut plus propager d'exception. Et un appelant qui ignore `error`
 * ne compile pas — `payload` est optionnel, les types Claude exigent des champs définis.
 *
 * ELLE SERT AUSSI SUR MOBILE, et ce n'est pas un effet de bord : un fichier illisible, supprimé
 * entre la sélection et la lecture, ou un PDF corrompu échouaient déjà là, tout aussi muettement.
 *
 * La conversion `kind → pdf|docx` vit ici plutôt qu'au six endroits qui la recopiaient : une
 * frontière qui rend déjà la bonne forme évite à ses appelants de la reconstruire.
 */
export async function lireDocumentImporte(file: PickedFile): Promise<LectureDocument> {
  const data = await lireBase64(file.uri);
  // `null` = échec, quelle que soit la plateforme : la primitive ne lève jamais (voir ses deux
  // faces). Le détail technique n'aiderait personne ; l'issue, si — et tous les écrans concernés
  // offrent le copier-coller comme second chemin.
  if (!data) return { error: 'Ce fichier n’a pas pu être lu. Réessaie, ou copie-colle son texte.' };
  return { payload: file.kind === 'pdf' ? { pdf: { data } } : { docx: { data } } };
}
